import { z } from 'zod';
import { entityBase, id, paginatedResult, paginationQuery, timestamp } from './common.js';

/**
 * Notification records. Delivery (email/SMS) is performed by the platform; this table tracks the
 * intent, channel, delivery status, and in-app read state.
 */
export const notificationChannel = z.enum(['email', 'sms', 'in_app']);
export type NotificationChannel = z.infer<typeof notificationChannel>;

/** User-facing groups used to filter the inbox and manage delivery preferences. */
export const NOTIFICATION_CATEGORIES = [
  'shift_updates',
  'shift_reminders',
  'credential_updates',
  'team_updates',
  'system',
] as const;
export const notificationCategory = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof notificationCategory>;

export const notificationStatus = z.enum(['pending', 'processing', 'sent', 'failed', 'read']);
export type NotificationStatus = z.infer<typeof notificationStatus>;

export const notification = entityBase.extend({
  recipientUserId: id,
  /** e.g. "invite.sent", "shift.confirmed", "credential.expiring". */
  type: z.string().min(1),
  title: z.string().min(1),
  body: z.string(),
  category: notificationCategory,
  channel: notificationChannel,
  status: notificationStatus,
  relatedEntityType: z.string().nullable(),
  relatedEntityId: id.nullable(),
  dedupeKey: z.string().nullable(),
  attemptCount: z.number().int().nonnegative(),
  nextAttemptAt: timestamp.nullable(),
  providerMessageId: z.string().nullable(),
  lastError: z.string().nullable(),
  sentAt: timestamp.nullable(),
  readAt: timestamp.nullable(),
});
export type Notification = z.infer<typeof notification>;

export const createNotificationInput = z.object({
  organizationId: z.string().min(1),
  recipientUserId: id,
  type: z.string().min(1),
  title: z.string().min(1),
  body: z.string(),
  category: notificationCategory.default('system'),
  channel: notificationChannel,
  relatedEntityType: z.string().nullish(),
  relatedEntityId: id.nullish(),
  dedupeKey: z.string().nullish(),
});
export type CreateNotificationInput = z.infer<typeof createNotificationInput>;

/** Immutable event used by Layer 1 to enforce per-user action limits. */
export const actionRateLimitEvent = z.object({
  id,
  organizationId: z.string().min(1),
  actorUserId: id,
  action: z.string().min(1),
  occurredAt: timestamp,
});
export type ActionRateLimitEvent = z.infer<typeof actionRateLimitEvent>;

export const smsNotificationPreference = z.object({
  id,
  organizationId: z.string().min(1),
  userId: id,
  phoneE164: z.string().nullable(),
  enabled: z.boolean(),
  consentedAt: timestamp.nullable(),
  revokedAt: timestamp.nullable(),
  consentSource: z.string().nullable(),
  consentVersion: z.string().nullable(),
  createdAt: timestamp,
  updatedAt: timestamp,
});
export type SmsNotificationPreferenceRecord = z.infer<typeof smsNotificationPreference>;

export const updateSmsNotificationPreferenceInput = z
  .object({
    phoneE164: z
      .string()
      .regex(/^\+[1-9]\d{7,14}$/)
      .nullable(),
    enabled: z.boolean(),
  })
  .refine((value) => !value.enabled || value.phoneE164 !== null, {
    message: 'A valid E.164 phone number is required to enable SMS',
    path: ['phoneE164'],
  });
export type UpdateSmsNotificationPreferenceInput = z.infer<
  typeof updateSmsNotificationPreferenceInput
>;

export const notificationPreferenceChannel = z.enum(['in_app', 'sms']);
export type NotificationPreferenceChannel = z.infer<typeof notificationPreferenceChannel>;

export const notificationPreference = entityBase.extend({
  userId: id,
  category: notificationCategory,
  channel: notificationPreferenceChannel,
  enabled: z.boolean(),
});
export type NotificationPreference = z.infer<typeof notificationPreference>;

export const notificationCategoryPreference = z.object({
  category: notificationCategory,
  inAppEnabled: z.boolean(),
  smsEnabled: z.boolean(),
});
export type NotificationCategoryPreference = z.infer<typeof notificationCategoryPreference>;

export const notificationPreferenceState = z.object({
  sms: updateSmsNotificationPreferenceInput,
  categories: z.array(notificationCategoryPreference),
});
export type NotificationPreferenceState = z.infer<typeof notificationPreferenceState>;

export const updateNotificationPreferencesInput = z
  .object({
    sms: updateSmsNotificationPreferenceInput.optional(),
    categories: z.array(notificationCategoryPreference).min(1).optional(),
  })
  .refine((value) => value.sms !== undefined || value.categories !== undefined, {
    message: 'At least one notification preference is required',
  });
export type UpdateNotificationPreferencesInput = z.infer<typeof updateNotificationPreferencesInput>;

export const notificationListQuery = paginationQuery.extend({
  category: notificationCategory.optional(),
  unreadOnly: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
});
export type NotificationListQuery = z.infer<typeof notificationListQuery>;

export const notificationPage = paginatedResult(notification).extend({
  unreadCount: z.number().int().nonnegative(),
});
export type NotificationPage = z.infer<typeof notificationPage>;
